import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { PAY_METHOD } from 'constants/payment';
import { usePayment, useTranslate, useSelectCard, useAmount } from 'hooks';
import { useAppSelector } from 'store/hooks';
import { useGetCertQuery, useSendCheckMutation } from 'store/payment/service';
import { costFormatWithCurrency } from 'utils/cost-format';
import { ecryptedWithJwkPublickKey } from 'utils/crypto-rsa';
import { SelectCard } from 'views/components/sz/select-card';
import { Button } from 'views/components/sz/ui/button';
import { Checkbox } from 'views/components/sz/ui/checkbox';
import { ApplePay } from 'views/forms/base/apple-pay';
import { GooglePay } from 'views/forms/base/google-pay';

import { EmailField } from '../email-field';

import { CardDetails, type CardFieldsType } from './card-details';

const CardForm = () => {
  const { paymentData, amount, isPayment, acquiring, fullAmount } = useAppSelector(
    (state) => state.payment,
  );
  const { data: cert } = useGetCertQuery(null);
  const [isSaveCard, setIsSaveCard] = useState(false);
  const [isEmail, setIsEmail] = useState(false);
  const { fetchSendPayment, isLoading } = usePayment();
  const [check] = useSendCheckMutation();
  const { addCard, removeCard, cards, selectedCard, setSelectedCard } = useSelectCard();
  const { currentAmountForPayment, isValidCurrentAmount } = useAmount();

  const isExpressPayment = useMemo(() => {
    if (paymentData) {
      return paymentData.acquirings.some(
        (item) => item.formType === PAY_METHOD.GOOGLE_PAY || item.formType === PAY_METHOD.APPLE_PAY,
      );
    }

    return false;
  }, [paymentData]);

  const methods = useForm<CardFieldsType>({
    mode: 'onChange',
    defaultValues: isPayment
      ? {
          pan: '',
          validity: '',
          cvv: '',
          email: '',
        }
      : { pan: '', email: '' },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, dirtyFields, isValid },
    setFocus,
    reset,
    trigger,
  } = methods;

  const { t } = useTranslate();

  useEffect(() => {
    if (!errors.pan && dirtyFields?.pan) {
      setFocus('validity');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors.pan]);

  useEffect(() => {
    if (!errors.validity && !errors.pan && dirtyFields.pan && dirtyFields.validity) {
      setFocus('cvv');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors.pan, errors.validity]);

  useEffect(() => {
    if (!selectedCard.newCard) {
      reset();
      setIsSaveCard(false);
    }

    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCard, isEmail]);

  const sendPayment = async (data: CardFieldsType) => {
    if (cert) {
      const cardData = {
        ...data,
        pan: data.pan.replace(/\s/g, ''),
      };
      const crypt = await ecryptedWithJwkPublickKey(cert, JSON.stringify(cardData));

      const payData = {
        phone: '',
        email: '',
        description: '',
        ID: paymentData?.ID,
        acquiringID: acquiring?.ID,
        amount: currentAmountForPayment,
        bankBin: cardData.pan ? cardData.pan.slice(0, 6) : null,
        cardData: crypt,
      };

      if (!selectedCard.newCard && selectedCard.crypt && selectedCard.bankBin) {
        payData.cardData = selectedCard.crypt;
        payData.bankBin = selectedCard.bankBin;
      }

      await fetchSendPayment(payData).then((res) => {
        if (isEmail) {
          const checkData = {
            ID: res.ID,
            email: data.email,
          };

          check({ ID: res.ID, check: checkData });
        }

        if (crypt && isSaveCard) {
          addCard({
            crypt,
            pan: cardData.pan,
          });
        }
      });
    }
  };

  const handleSetIsEmail = () => {
    setIsEmail(!isEmail);
  };

  const handleSetIsSaveCard = () => {
    setIsSaveCard(!isSaveCard);
  };

  const renderExpressPaymentBtns = () => {
    return (
      <div className="flex gap-3 flex-1 px-4 py-3 md:px-0 md:pb-0 md:pt-6 bg-white">
        {paymentData?.acquirings.map((item) => {
          if (item.formType === PAY_METHOD.GOOGLE_PAY) {
            return <GooglePay key={item.ID} acquiringID={item.ID} />;
          }

          if (item.formType === PAY_METHOD.APPLE_PAY) {
            return <ApplePay key={item.ID} acquiringID={item.ID} />;
          }

          return null;
        })}
      </div>
    );
  };

  return paymentData ? (
    <FormProvider {...methods}>
      <form className="md:p-6 flex flex-col">
        <div className="flex flex-col gap-6 bg-white px-4 py-3 md:px-0 md:pb-6 md:pt-0">
          <div className="text-base leading-4 font-semibold text-sz-black">
            {isPayment ? 'Сумма пополнения: ' : 'Сумма выплаты: '}
            <span className="text-sz-blue-100">
              {costFormatWithCurrency(
                paymentData?.fix ? Math.abs(Number(paymentData.amount)) : Number(amount),
                'KZT',
              )}
            </span>
          </div>
        </div>
        {isExpressPayment && <div>{renderExpressPaymentBtns()}</div>}

        {isExpressPayment && acquiring && (
          <div className="flex items-center gap-8 px-4 md:px-0 md:py-6 text-sz-gray-700 bg-white">
            <div className="max-w-[200px] w-full h-[1px] bg-sz-gray-200" />
            <span>или</span>
            <div className="max-w-[200px] w-full h-[1px] bg-sz-gray-200" />
          </div>
        )}

        {acquiring && (
          <div className="flex flex-col gap-3 md:gap-6">
            <div className="px-4 md:px-0 flex flex-col gap-[22px] md:gap-6 bg-white py-3 md:py-0">
              <h3 className="text-[18px] leading-5 font-bold">{acquiring.title}</h3>
              <div>
                <SelectCard
                  handleDelete={(pan) => removeCard(pan)}
                  cards={cards}
                  selectedCard={selectedCard}
                  setSelectedCard={setSelectedCard}
                />
              </div>
              <CardDetails control={control} selectedCard={selectedCard} />
            </div>
            <div className="px-4 md:px-0 flex flex-col gap-3 md:gap-6">
              <div className="flex flex-col gap-3 md:gap-6">
                {selectedCard.newCard && (
                  <Checkbox
                    name="save-card"
                    label="Сохранить карту"
                    checked={isSaveCard}
                    onChange={handleSetIsSaveCard}
                  />
                )}
                <Checkbox
                  checked={isEmail}
                  name="send-email"
                  onChange={handleSetIsEmail}
                  label="Прислать на мой E-mail информацию об оплате"
                />
              </div>
              {isEmail && (
                <div>
                  <EmailField />
                </div>
              )}
              <Button
                onClick={handleSubmit(sendPayment)}
                disabled={!isValid || !isValidCurrentAmount}
                isLoading={isLoading}
              >
                {isPayment ? t('pay') : t('pay_out')}{' '}
                {fullAmount && isValid ? `${fullAmount} KZT` : null}
              </Button>
            </div>
          </div>
        )}
      </form>
    </FormProvider>
  ) : null;
};

export { CardForm };
